import {useMemo} from 'react'
import {useParams} from 'react-router-dom'
import {Download} from 'lucide-react'
import {useI18n} from '@/i18n'
import {Stack} from '@/components/shared/Stack'
import {Icon} from '@/components/shared/Icon'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {DetailField} from '@/components/shared/DetailField'
import {MonoText} from '@/components/shared/MonoText'
import {MutedText} from '@/components/shared/MutedText'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {PageLayout} from '@/components/shared/PageLayout'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {useTrace} from '@/hooks/use-observability'
import {formatTimestamp} from '@/lib/date-utils'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import type {Span} from '@/types/observability'
import {SpanKind} from '@/types/observability'

interface RequestRow {
    span: Span
    sqlCount: number
    totalSqlMs: number
}

function collectChildSpanIds(spans: Span[], rootId: string): Set<string> {
    const childIds = new Set<string>()
    const queue = [rootId]
    while (queue.length > 0) {
        const current = queue.shift()!
        for (const span of spans) {
            if (span.parentSpanId === current && !childIds.has(span.spanId)) {
                childIds.add(span.spanId)
                queue.push(span.spanId)
            }
        }
    }
    return childIds
}

function buildRequestRows(spans: Span[]): RequestRow[] {
    const httpRequests = spans.filter(s => s.kind === SpanKind.HTTP_REQUEST)
    return httpRequests.map(req => {
        const childIds = collectChildSpanIds(spans, req.spanId)
        const childSpans = spans.filter(s => childIds.has(s.spanId))
        const sqlSpans = childSpans.filter(s => s.kind === SpanKind.SQL)
        const sqlCount = sqlSpans.length
        const totalSqlMs = sqlSpans.reduce((sum, s) => sum + s.durationMs, 0)
        return {span: req, sqlCount, totalSqlMs}
    }).sort((a, b) => a.span.startedAt.localeCompare(b.span.startedAt))
}

function statusBadgeVariant(code: number | null): BadgeVariant {
    if (code === null) return BadgeVariant.OUTLINE
    if (code >= 500) return BadgeVariant.DESTRUCTIVE
    if (code >= 400) return BadgeVariant.WARNING
    if (code >= 200 && code < 300) return BadgeVariant.SUCCESS
    return BadgeVariant.OUTLINE
}

export function RequestInspectorDetail() {
    const i18n = useI18n()

    const {sessionId} = useParams<{sessionId: string}>()
    const decodedSessionId = sessionId ? decodeURIComponent(sessionId) : undefined

    const {data: trace, isLoading, error} = useTrace(decodedSessionId)

    const requestRows = useMemo(() => {
        if (!trace) return []
        return buildRequestRows(trace.spans)
    }, [trace])

    const handleDownload = () => {
        if (!trace) return
        /* eslint-disable-next-line i18next/no-literal-string -- MIME type */
        const blob = new Blob([JSON.stringify(trace, null, 2)], {type: 'application/json'})
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `trace-${trace.sessionId}.json`
        a.click()
        URL.revokeObjectURL(url)
    }

    return (
        <PageLayout
            data-testid="request-inspector-detail-page"
            variant="detail"
            title={trace?.sessionId ?? i18n.requestInspector.detail.title}
            icon={CONCEPT_ICON.requestInspector}
            loading={isLoading}
            error={error ?? (!isLoading ? notFoundIfMissing(trace, 'REQUEST_INSPECTOR') : undefined)}
            backTo={routes.admin.requestInspector.list()}
            actions={trace ? (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownload}
                    data-testid="btn-download-trace"
                >
                    <Icon icon={Download} className="mr-2"/>
                    {i18n.requestInspector.detail.downloadTrace}
                </Button>
            ) : undefined}
        >
            {trace && <>
            <Stack gap="lg" data-testid="request-inspector-detail-page">
                <Card>
                    <CardHeader>
                        <CardTitle data-testid="card-title-detail">{i18n.requestInspector.detail.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <DetailGrid columns={2} responsive>
                            <DetailField
                                label={i18n.requestInspector.detail.sessionId}
                                value={<MonoText className="break-all">{trace.sessionId}</MonoText>}
                            />
                            <DetailField
                                label={i18n.requestInspector.detail.totalDuration}
                                 
                                value={`${trace.durationMs}ms`}
                            />
                            <DetailField
                                label={i18n.requestInspector.detail.startedAt}
                                value={formatTimestamp(trace.startedAt)}
                            />
                            <DetailField
                                label={i18n.requestInspector.detail.endedAt}
                                value={formatTimestamp(trace.endedAt)}
                            />
                            {trace.e2eRunId && (
                                <DetailField
                                    label={i18n.requestInspector.detail.e2eRunId}
                                    value={<MonoText>{trace.e2eRunId}</MonoText>}
                                />
                            )}
                            {trace.e2eTestClass && (
                                <DetailField
                                    label={i18n.requestInspector.detail.e2eTestClass}
                                    value={<MonoText>{trace.e2eTestClass}</MonoText>}
                                />
                            )}
                            {trace.e2eTestMethod && (
                                <DetailField
                                    label={i18n.requestInspector.detail.e2eTestMethod}
                                    value={<MonoText>{trace.e2eTestMethod}</MonoText>}
                                />
                            )}
                        </DetailGrid>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle data-testid="card-title-requests">{i18n.requestInspector.detail.requestsTitle}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Table data-testid="requests-table">
                            <TableHeader>
                                <TableRow>
                                    <TableHead data-testid="col-method" className="w-20">
                                        {i18n.requestInspector.detail.columns.method}
                                    </TableHead>
                                    <TableHead data-testid="col-url">
                                        {i18n.requestInspector.detail.columns.url}
                                    </TableHead>
                                    <TableHead data-testid="col-status" className="w-20 text-right">
                                        {i18n.requestInspector.detail.columns.status}
                                    </TableHead>
                                    <TableHead data-testid="col-duration" className="w-28 text-right">
                                        {i18n.requestInspector.detail.columns.duration}
                                    </TableHead>
                                    <TableHead data-testid="col-sql-count" className="w-28 text-right">
                                        {i18n.requestInspector.detail.columns.sqlCount}
                                    </TableHead>
                                    <TableHead data-testid="col-sql-time" className="w-28 text-right">
                                        {i18n.requestInspector.detail.columns.sqlTime}
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {requestRows.length === 0 ? (
                                    <TableEmptyState
                                        colSpan={6}
                                        hasFilters={false}
                                        emptyMessage={i18n.requestInspector.detail.noRequests as string}
                                        matchMessage={i18n.requestInspector.detail.noRequests as string}
                                    />
                                ) : (
                                    requestRows.map(({span, sqlCount, totalSqlMs}) => (
                                        <ClickableTableRow
                                            key={span.spanId}
                                            to={routes.admin.requestInspector.requestDetail(
                                                encodeURIComponent(trace.sessionId),
                                                span.spanId,
                                            )}
                                            data-testid={`request-row-${span.spanId}`}
                                        >
                                            <TableCell>
                                                <Badge variant="outline" className="font-mono text-xs">
                                                    {span.httpMethod ?? '—'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="max-w-xs">
                                                <MonoText size="xs" className="truncate block" title={span.httpUrl ?? undefined}>
                                                    {span.httpUrl ?? '—'}
                                                </MonoText>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {span.httpStatusCode !== null ? (
                                                    <Badge variant={statusBadgeVariant(span.httpStatusCode)}>
                                                        {span.httpStatusCode}
                                                    </Badge>
                                                ) : (
                                                    <MutedText>—</MutedText>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {/* eslint-disable-next-line i18next/no-literal-string -- unit suffix */}
                                                <MutedText>{span.durationMs}ms</MutedText>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {sqlCount > 0 ? (
                                                    <Badge variant="outline">{sqlCount}</Badge>
                                                ) : (
                                                    <MutedText>0</MutedText>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {/* eslint-disable-next-line i18next/no-literal-string -- unit suffix */}
                                                <MutedText>{totalSqlMs > 0 ? `${totalSqlMs}ms` : '—'}</MutedText>
                                            </TableCell>
                                        </ClickableTableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </Stack>
        </>}
        </PageLayout>
    )
}
